import { ProgramType } from 'fitbeat.models';
import React from 'react';
import { translate } from 'react-i18next';
import { Image, StyleSheet, Text, TouchableOpacity, View } from 'react-native';
import { Button, ButtonTypes } from '../../components';
import Theme from '../../theme';
import { TrackingGauge } from '../../ui/components/TrackingGauge';
import { defaultScale, scaleText } from '../../utils/scaling';
import { mappedTitles } from '../../WorkoutGoalAssistant';

interface IProps {
    t: (key: string, options?: any) => string;
    bodyScanStats: any;
    longTermGoal: ProgramType;
    isScanContinuingFromWeightTarget: boolean;
    isReviewingTargetAndSavingScan: boolean;
    saveBodyScan(): void;
    redoBodyAssessment(): void;
    continueToSetWeightTarget(): void;
}

const mappedGaugeOrders = {
    [ProgramType.GetLeanAndFitGoal]:
        ['fatTrackingGaugeData', 'totalBodyWeightTrackingGaugeData', 'muscleMassTrackingGaugeData'],
    [ProgramType.GainMuscleAndStrengthGoal]:
        ['totalBodyWeightTrackingGaugeData', 'muscleMassTrackingGaugeData', 'fatTrackingGaugeData'],
    [ProgramType.GetStrongAndFitGoal]:
        ['totalBodyWeightTrackingGaugeData', 'fatTrackingGaugeData', 'muscleMassTrackingGaugeData'],
    [ProgramType.MobilityAndTone]:
        ['totalBodyWeightTrackingGaugeData', 'fatTrackingGaugeData', 'muscleMassTrackingGaugeData'],
    [ProgramType.MuscleAndShred]:
        ['totalBodyWeightTrackingGaugeData', 'fatTrackingGaugeData', 'muscleMassTrackingGaugeData'],
};

class BodyscaleScanSummary extends React.PureComponent<IProps, any> {

    constructor(props: IProps) {
        super(props);
    }

    public render() {
        const {
            t,
            bodyScanStats,
            saveBodyScan,
            redoBodyAssessment,
            longTermGoal,
            isScanContinuingFromWeightTarget,
            continueToSetWeightTarget,
            isReviewingTargetAndSavingScan,
        } = this.props;
        const { totalBodyWeightTrackingGaugeData, muscleMassTrackingGaugeData, fatTrackingGaugeData } = bodyScanStats;
        const gaugeOrder = mappedGaugeOrders[longTermGoal];
        return (
            <View style={styles.container}>
                <View style={styles.horizontalSeparator} />
                <View style={styles.iconContainer}>
                    <Image
                        style={styles.completeIcon}
                        source={require('../../assets/images/complete-icon.png')}
                    />
                </View>
                <Text style={styles.scanCompleteText}>{t('scanComplete')}</Text>
                <View style={styles.horizontalSeparator} />
                <Text style={styles.goalText}>{`${t('goal')}: ${mappedTitles[longTermGoal]}`}</Text>
                <Text style={styles.resultSummaryTitleText}>{t('scanResultSummary')}</Text>
                <View style={styles.healthBodyContainer}>
                    {totalBodyWeightTrackingGaugeData &&
                        <TrackingGauge
                            data={bodyScanStats[gaugeOrder[0]].data}
                            valAndUnit={bodyScanStats[gaugeOrder[0]].valAndUnit}
                            valuePercent={bodyScanStats[gaugeOrder[0]].valuePercent}
                            radius={defaultScale(33)}
                            smallCircleRadius={defaultScale(6)}
                            strokeWidth={defaultScale(6)}
                            centerTextStyle={styles.smallGaugeCenterText}
                            bottomTextStyle={styles.smallGaugeBottomTextStyle}
                            bottomText={bodyScanStats[gaugeOrder[0]].label}
                        />
                    }
                    {muscleMassTrackingGaugeData &&
                        <TrackingGauge
                            data={bodyScanStats[gaugeOrder[1]].data}
                            valAndUnit={bodyScanStats[gaugeOrder[1]].valAndUnit}
                            valuePercent={bodyScanStats[gaugeOrder[1]].valuePercent}
                            radius={defaultScale(53)}
                            strokeWidth={defaultScale(10)}
                            centerTextStyle={styles.kingMetricGaugeText}
                            bottomTextStyle={styles.kingMetricGaugeBottomTextStyle}
                            bottomText={bodyScanStats[gaugeOrder[1]].label}
                            smallCircleRadius={defaultScale(8)}
                        />
                    }
                    {fatTrackingGaugeData &&
                        <TrackingGauge
                            data={bodyScanStats[gaugeOrder[2]].data}
                            valAndUnit={bodyScanStats[gaugeOrder[2]].valAndUnit}
                            valuePercent={bodyScanStats[gaugeOrder[2]].valuePercent}
                            radius={defaultScale(32)}
                            smallCircleRadius={defaultScale(6)}
                            strokeWidth={defaultScale(6)}
                            centerTextStyle={styles.smallGaugeCenterText}
                            bottomTextStyle={styles.smallGaugeBottomTextStyle}
                            bottomText={bodyScanStats[gaugeOrder[2]].label}
                        />
                    }
                </View>
                <Text style={styles.kingMetricDescriptiionText}>{t('kingMetricDescription')}</Text>
                <View style={styles.horizontalSeparator} />
                <View style={{ flex: 1, justifyContent: 'flex-end', width: '100%' }}>
                    <Button
                        title={isScanContinuingFromWeightTarget ? t('continueToSetYourTarget') : "Save"}
                        onPress={isScanContinuingFromWeightTarget ? continueToSetWeightTarget : saveBodyScan}
                        fontColor={Theme.WHITE}
                        buttonType={ButtonTypes.NORMAL}
                        containerStyle={[styles.submitButtonContainer]}
                        isLoading={isReviewingTargetAndSavingScan}
                    />
                    <TouchableOpacity onPress={redoBodyAssessment}>
                        <Text style={[styles.repeatAssessmentText, { color: Theme.PRIMARY }]}>
                            {t('fineTuneAndRepeatAssessment')}
                        </Text>
                    </TouchableOpacity>
                </View>
            </View>
        );
    }
}

const styles = StyleSheet.create({
    container: {
        flex: 1,
        alignItems: 'center',

    },
    iconContainer: {
        marginTop: defaultScale(16),
        marginBottom: defaultScale(5),
    },
    completeIcon: {
        width: defaultScale(48),
        height: defaultScale(48),
    },
    scanCompleteText: {
        ...Theme.textStyle.heading,
        fontSize: scaleText(25),
        marginBottom: defaultScale(30),
        color: Theme.GREY_DARK,
    },
    submitButtonContainer: {
        marginVertical: defaultScale(22),
        width: '100%',
    },
    horizontalSeparator: {
        width: '100%',
        height: defaultScale(1),
        backgroundColor: Theme.GREY_FIGMA_MEDIUM_LIGHT,
        opacity: 0.18,
    },
    resultSummaryTitleText: {
        fontFamily: Theme.Weight700,
        color: Theme.CHARCOAL,
        marginBottom: defaultScale(18),
        textAlign: 'center',
        fontSize: scaleText(14),
    },
    healthBodyContainer: {
        width: '100%',
        flexDirection: 'row',
        justifyContent: 'space-evenly',
        alignItems: 'center',
        paddingBottom: defaultScale(20),
    },
    smallGaugeCenterText: {
        fontFamily: Theme.Weight700,
        fontSize: scaleText(16),
        color: Theme.DARK_CHARCOAL,
    },
    kingMetricGaugeText: {
        fontFamily: Theme.Weight700,
        fontSize: scaleText(22),
        color: Theme.DARK_CHARCOAL,
    },
    smallGaugeBottomTextStyle: {
        textAlign: 'center',
        marginTop: defaultScale(-5),
        fontFamily: Theme.Weight500,
        fontSize: scaleText(11),
        color: Theme.DARK_CHARCOAL,
    },
    kingMetricGaugeBottomTextStyle: {
        textAlign: 'center',
        marginTop: defaultScale(-5),
        fontFamily: Theme.Weight500,
        fontSize: scaleText(20),
        color: Theme.DARK_CHARCOAL,
    },
    kingMetricDescriptiionText: {
        ...Theme.textStyle.bodyCopyReg,
        color: Theme.GREY_MEDIUM,
        marginTop: defaultScale(6),
        marginBottom: defaultScale(20),
    },
    goalText: {
        textAlign: 'center',
        textTransform: 'uppercase',
        fontFamily: Theme.Weight700,
        fontSize: defaultScale(20),
        marginTop: defaultScale(34),
        marginBottom: defaultScale(24),
    },
    repeatAssessmentText: {
        ...Theme.textStyle.bodyCopyReg,
        fontSize: scaleText(12),
        fontWeight: '700',
        paddingHorizontal: defaultScale(30),
        textAlign: 'center',
    },
});

export default translate(['bodyscan'])(BodyscaleScanSummary);
